let audioCtx = null;
let userActivated = false;
let sharedAudio = null;

/** Tiny multi-tone WAV so Android WebView plays even when Oscillator is flaky. */
const BEEP_DATA_URI = (() => {
  const sampleRate = 22050;
  const durationSec = 0.85;
  const numSamples = Math.floor(sampleRate * durationSec);
  const dataSize = numSamples * 2;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  const writeStr = (offset, str) => {
    for (let i = 0; i < str.length; i += 1) view.setUint8(offset + i, str.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, 'data');
  view.setUint32(40, dataSize, true);

  for (let i = 0; i < numSamples; i += 1) {
    const t = i / sampleRate;
    let amp = 0;
    if (t < 0.18) amp = Math.sin(2 * Math.PI * 880 * t);
    else if (t > 0.22 && t < 0.4) amp = Math.sin(2 * Math.PI * 880 * t);
    else if (t > 0.46 && t < 0.82) amp = Math.sin(2 * Math.PI * 1175 * t);
    const envelope = Math.min(1, t * 30, (durationSec - t) * 20);
    const sample = Math.max(-1, Math.min(1, amp * envelope * 0.9));
    view.setInt16(44 + i * 2, sample * 0x7fff, true);
  }

  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return `data:audio/wav;base64,${btoa(binary)}`;
})();

export const unlockAudio = () => {
  try {
    userActivated = true;
    if (!audioCtx) {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtx.state === 'suspended') void audioCtx.resume();
    if (!sharedAudio) {
      sharedAudio = new Audio(BEEP_DATA_URI);
      sharedAudio.preload = 'auto';
      sharedAudio.volume = 1;
    }
    // Prime playback on a user gesture (silent).
    const a = sharedAudio.cloneNode();
    a.volume = 0.01;
    const p = a.play();
    if (p?.then) p.then(() => { a.pause(); a.currentTime = 0; }).catch(() => {});
  } catch { /* ignore */ }
};

if (typeof document !== 'undefined') {
  const once = () => {
    unlockAudio();
    document.removeEventListener('touchstart', once);
    document.removeEventListener('click', once);
  };
  document.addEventListener('touchstart', once, { passive: true });
  document.addEventListener('click', once);
}

let alertInterval = null;
let vibrateInterval = null;

const safeVibrate = (pattern) => {
  if (typeof navigator === 'undefined' || !navigator.vibrate) return;
  try { navigator.vibrate(pattern); } catch { /* blocked */ }
};

const playWavBeep = () => {
  try {
    const a = new Audio(BEEP_DATA_URI);
    a.volume = 1;
    const p = a.play();
    if (p?.catch) p.catch(() => {});
    return true;
  } catch {
    return false;
  }
};

const playOscillatorBeep = () => {
  try {
    unlockAudio();
    if (!audioCtx) return;
    const beep = (freq, start, duration) => {
      const o = audioCtx.createOscillator();
      const g = audioCtx.createGain();
      o.connect(g);
      g.connect(audioCtx.destination);
      o.frequency.value = freq;
      o.type = 'square';
      g.gain.setValueAtTime(0, audioCtx.currentTime + start);
      g.gain.linearRampToValueAtTime(0.85, audioCtx.currentTime + start + 0.02);
      g.gain.linearRampToValueAtTime(0, audioCtx.currentTime + start + duration);
      o.start(audioCtx.currentTime + start);
      o.stop(audioCtx.currentTime + start + duration + 0.05);
    };
    beep(950, 0, 0.18);
    beep(950, 0.22, 0.18);
    beep(1250, 0.44, 0.35);
  } catch { /* autoplay blocked */ }
};

export const playAlertSound = () => {
  unlockAudio();
  const ok = playWavBeep();
  if (!ok) {
    playOscillatorBeep();
  }
  safeVibrate([400, 200, 400]);
};

export const startContinuousAlert = () => {
  stopContinuousAlert();
  unlockAudio();
  playAlertSound();
  alertInterval = setInterval(() => {
    playAlertSound();
  }, 1400);

  safeVibrate([500, 300, 500, 300, 500]);
  vibrateInterval = setInterval(() => {
    safeVibrate([500, 300, 500, 300, 500]);
  }, 2000);
};

export const stopContinuousAlert = () => {
  if (alertInterval) {
    clearInterval(alertInterval);
    alertInterval = null;
  }
  if (vibrateInterval) {
    clearInterval(vibrateInterval);
    vibrateInterval = null;
  }
  safeVibrate(0);
};

export const isVoiceAnnouncementEnabled = () => {
  if (typeof localStorage === 'undefined') return true;
  return localStorage.getItem('mt_driver_voice_enabled') !== '0';
};

export const setVoiceAnnouncementEnabled = (enabled) => {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem('mt_driver_voice_enabled', enabled ? '1' : '0');
};

export const speakRideAnnouncement = ({ pickup = '', fare = 0, lang = 'te' } = {}) => {
  if (!isVoiceAnnouncementEnabled()) return;
  try {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const cleanPickup = String(pickup || '').split(',')[0].slice(0, 40);
    const voices = window.speechSynthesis.getVoices() || [];
    const hasTeluguVoice = voices.some((v) => v.lang.includes('te'));

    const isTelugu = (lang === 'te' || !lang) && hasTeluguVoice;
    const text = isTelugu
      ? `కొత్త ఆటో ట్రిప్ వచ్చింది! పికప్: ${cleanPickup}. ఛార్జ్: ${fare} రూపాయలు.`
      : `New ride request! Pickup: ${cleanPickup}. Fare: ${fare} rupees.`;

    const msg = new SpeechSynthesisUtterance(text);
    msg.lang = isTelugu ? 'te-IN' : 'en-IN';
    msg.rate = 0.95;
    msg.pitch = 1.0;

    if (isTelugu) {
      const teluguVoice = voices.find((v) => v.lang.includes('te'));
      if (teluguVoice) msg.voice = teluguVoice;
    } else {
      const enVoice = voices.find((v) => v.lang.includes('en-IN'))
        || voices.find((v) => v.lang.startsWith('en'))
        || null;
      if (enVoice) msg.voice = enVoice;
    }

    window.speechSynthesis.speak(msg);
  } catch {
    /* speech synthesis unsupported or blocked */
  }
};
